import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, Wrench, Gauge, Wind, Waves, Plug, Calendar, CloudLightning, AlertTriangle,
    PaintBucket, ChefHat, Bath, Layers, Palette, Trees, Sofa, Sparkles,
    HardHat, MapPinned, Activity, ShieldCheck, FileSpreadsheet, MessageSquare, ClipboardList, Folder,
    Camera, FileText, Network, Wallet, CheckCircle2,
    Eye, HandshakeIcon, Megaphone, Building2, Award, Scale,
} from "lucide-react";

import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { useLanguage } from "@/lib/LanguageContext";
import useSEO from "@/lib/useSEO";
import { gaMaintenanceView } from "@/lib/analytics";

const MAINT_ICONS = [Wrench, Gauge, Wind, Waves, Plug, Calendar, CloudLightning, AlertTriangle];
const RENO_ICONS = [PaintBucket, ChefHat, Bath, Layers, Palette, Trees, Sofa, Sparkles];
const CONSTRUCT_ICONS = [HardHat, MapPinned, Activity, ShieldCheck, FileSpreadsheet, MessageSquare, ClipboardList, Folder];
const REPORT_ICONS = [Camera, FileText, Network, Wallet, CheckCircle2];
const WHY_ICONS = [Eye, HandshakeIcon, Megaphone, Building2, Award, Scale];

const MAINT_KEYS = ["m1", "m2", "m3", "m4", "m5", "m6", "m7", "m8"];
const RENO_KEYS = ["r1", "r2", "r3", "r4", "r5", "r6", "r7", "r8"];
const CONSTRUCT_KEYS = ["c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8"];
const PROCESS_KEYS = ["ph1", "ph2", "ph3", "ph4", "ph5", "ph6"];
const REPORT_KEYS = ["rp1", "rp2", "rp3", "rp4", "rp5"];
const WHY_KEYS = ["w1", "w2", "w3", "w4", "w5", "w6"];

// =============================================================================
// IMAGERY — clearly labeled placeholders, easy to swap with real PKM photography.
// To replace: drop the new file into /app/frontend/public/maintenance/<filename>.jpg
// and change the URL string. Or paste a new Unsplash / CDN URL inline.
// Each constant includes a one-line creative direction describing the IDEAL real
// photo PKM should use here.
// =============================================================================

// HERO_IMAGE
// IDEAL: a wide, cinematic photograph of an immaculately FINISHED luxury Cabo
// residence — kitchen, living, or architectural exterior — communicating the
// end-result of PKM's project oversight. Avoid hard-hats / dust / construction.
const HERO_IMAGE = "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=2400&q=85";

// INTRO_IMAGE
// IDEAL: a portrait/vertical shot of architectural plans on a stone counter,
// a PKM supervisor reviewing drawings on an iPad, or an owner-rep moment on
// site. Communicates "your eyes and ears on the ground." Quiet, considered.
const INTRO_IMAGE = "https://images.unsplash.com/photo-1518733057094-95b53143d2a7?auto=format&fit=crop&w=1800&q=85";

// RENO_IMAGE
// IDEAL: a landscape image of a high-end FINISHED renovation — a luxury
// bathroom, kitchen refresh, or living-room refit — emphasising materiality
// and craftsmanship. Beautiful natural light preferred.
const RENO_IMAGE = "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&w=1800&q=85";

// CONSTRUCT_IMAGE
// IDEAL: a portrait/vertical shot of skilled craftsmanship — a millworker
// finishing cabinetry, stonework being set, or an architectural detail in
// progress. Refined, focused — not generic site / hard-hat imagery.
const CONSTRUCT_IMAGE = "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1800&q=85";

// REPORT_IMAGE
// IDEAL: a clean landscape shot of organized documentation — laptop with
// progress report, neatly stacked photo logs, or architectural drawings
// beside a notebook. Conveys transparency and discipline.
const REPORT_IMAGE = "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1800&q=85";

export default function MaintenanceConstruction() {
    const { t, lang } = useLanguage();

    // Fire maintenance_view exactly once per navigation.
    useEffect(() => { const t = setTimeout(() => gaMaintenanceView(), 200); return () => clearTimeout(t); }, []);

    useSEO({
        title: lang === "es"
            ? "Mantenimiento, Renovación y Construcción | Pacific Key Management"
            : "Maintenance, Renovation & Construction | Pacific Key Management",
        description: lang === "es"
            ? "Coordinación profesional de mantenimiento, supervisión de renovaciones y supervisión de construcción para residencias de lujo en Los Cabos."
            : "Professional maintenance coordination, renovation oversight and construction supervision for luxury residences throughout Los Cabos.",
        path: "/maintenance-construction",
        keywords: "Construction Management Los Cabos, Renovation Management Los Cabos, Luxury Home Renovation Los Cabos, Property Maintenance Los Cabos, Owner Representation Los Cabos, Villa Maintenance Los Cabos, Estate Management Los Cabos, Luxury Property Services Los Cabos",
        breadcrumbs: [
            { name: "Home", path: "/" },
            { name: "Maintenance & Construction", path: "/maintenance-construction" },
        ],
        schema: {
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: "Maintenance, Renovation & Construction Oversight",
            provider: { "@type": "Organization", name: "Pacific Key Management", url: "https://pacifickeymanagement.com" },
            areaServed: { "@type": "Place", name: "Los Cabos, Baja California Sur, Mexico" },
            url: "https://pacifickeymanagement.com/maintenance-construction",
        },
    });

    return (
        <div data-testid="maintenance-construction-page">
            <PageHero
                eyebrow={t("maintConstruct.heroEyebrow")}
                title={<>{t("maintConstruct.heroTitle1")} <span className="italic font-light">{t("maintConstruct.heroTitle2")}</span></>}
                subtitle={t("maintConstruct.heroSubtitle")}
                image={HERO_IMAGE}
            />

            {/* SECTION 1 — Owner Rep intro */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="mc-intro-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    <Reveal className="lg:col-span-6">
                        <div className="aspect-[5/6] overflow-hidden">
                            <img src={INTRO_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <Reveal delay={120} className="lg:col-span-6 space-y-7">
                        <p className="overline text-gold">{t("maintConstruct.introEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-balance">{t("maintConstruct.introTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("maintConstruct.introP1")}</p>
                        <p className="text-navy/75 leading-relaxed">{t("maintConstruct.introP2")}</p>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 2 — Maintenance */}
            <section className="bg-sand/40 py-24 sm:py-32" data-testid="mc-maintenance-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-16">
                        <p className="overline text-gold">{t("maintConstruct.maintEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("maintConstruct.maintTitle")}</h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {MAINT_KEYS.map((k, i) => {
                            const Icon = MAINT_ICONS[i] || Wrench;
                            return (
                                <Reveal key={k} delay={i * 60} className="bg-pearl p-9 min-h-[260px] hover:bg-pearl/70 transition-colors duration-500" data-testid={`mc-maint-${i}`}>
                                    <Icon className="w-7 h-7 text-gold" strokeWidth={1.25} />
                                    <h3 className="mt-7 font-serif text-xl leading-tight">{t(`maintConstruct.${k}.title`)}</h3>
                                    <span className="block w-7 h-px bg-gold my-4" />
                                    <p className="text-sm text-navy/70 leading-relaxed">{t(`maintConstruct.${k}.body`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 3 — Renovation */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="mc-renovation-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start mb-16">
                        <Reveal className="lg:col-span-6">
                            <div className="aspect-[4/3] overflow-hidden">
                                <img src={RENO_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-6 space-y-6">
                            <p className="overline text-gold">{t("maintConstruct.renoEyebrow")}</p>
                            <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("maintConstruct.renoTitle")}</h2>
                            <span className="gold-divider" />
                            <p className="text-navy/75 leading-relaxed">{t("maintConstruct.renoBody")}</p>
                        </Reveal>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10">
                        {RENO_KEYS.map((k, i) => {
                            const Icon = RENO_ICONS[i] || PaintBucket;
                            return (
                                <Reveal key={k} delay={i * 60} className="bg-pearl p-8 min-h-[230px]" data-testid={`mc-reno-${i}`}>
                                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                                    <h3 className="mt-6 font-serif text-lg leading-tight">{t(`maintConstruct.${k}.title`)}</h3>
                                    <span className="block w-6 h-px bg-gold my-3" />
                                    <p className="text-sm text-navy/70 leading-relaxed">{t(`maintConstruct.${k}.body`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — Construction + Process Timeline */}
            <section className="bg-navy text-pearl py-24 sm:py-32" data-testid="mc-construction-section">
                <div className="container-luxe">
                    <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center mb-20">
                        <Reveal className="lg:col-span-7 space-y-6">
                            <p className="overline text-gold">{t("maintConstruct.constructEyebrow")}</p>
                            <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("maintConstruct.constructTitle")}</h2>
                            <span className="gold-divider" />
                            <p className="text-pearl/75 leading-relaxed max-w-xl">{t("maintConstruct.constructBody")}</p>
                        </Reveal>
                        <Reveal delay={120} className="lg:col-span-5">
                            <div className="aspect-[4/5] overflow-hidden">
                                <img src={CONSTRUCT_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        </Reveal>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-pearl/10 mb-24">
                        {CONSTRUCT_KEYS.map((k, i) => {
                            const Icon = CONSTRUCT_ICONS[i] || HardHat;
                            return (
                                <Reveal key={k} delay={i * 60} className="bg-navy p-8 min-h-[230px]" data-testid={`mc-construct-${i}`}>
                                    <Icon className="w-6 h-6 text-gold" strokeWidth={1.25} />
                                    <h3 className="mt-6 font-serif text-pearl text-lg leading-tight">{t(`maintConstruct.${k}.title`)}</h3>
                                    <span className="block w-6 h-px bg-gold my-3" />
                                    <p className="text-sm text-pearl/65 leading-relaxed">{t(`maintConstruct.${k}.body`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>

                    {/* Process timeline */}
                    <Reveal className="max-w-3xl mb-12">
                        <p className="overline text-gold">{t("maintConstruct.processEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-pearl text-3xl sm:text-4xl leading-tight">{t("maintConstruct.processTitle")}</h2>
                    </Reveal>

                    <div className="relative">
                        <span className="hidden lg:block absolute left-0 right-0 top-7 h-px bg-gold/30" />
                        <div className="grid grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-6">
                            {PROCESS_KEYS.map((k, i) => (
                                <Reveal key={k} delay={i * 80} data-testid={`mc-process-${i}`} className="relative">
                                    <div className="relative flex items-center justify-center w-14 h-14 mb-5 bg-navy border border-gold/60 z-10">
                                        <span className="font-serif text-gold text-xl">0{i + 1}</span>
                                    </div>
                                    <h3 className="font-serif text-pearl text-lg leading-snug mb-2">{t(`maintConstruct.${k}.title`)}</h3>
                                    <p className="text-xs text-pearl/60 leading-relaxed">{t(`maintConstruct.${k}.body`)}</p>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 5 — Reporting & Transparency */}
            <section className="bg-pearl py-24 sm:py-32" data-testid="mc-reporting-section">
                <div className="container-luxe grid lg:grid-cols-12 gap-14 items-start">
                    <Reveal className="lg:col-span-5 space-y-6 lg:sticky lg:top-32">
                        <p className="overline text-gold">{t("maintConstruct.reportEyebrow")}</p>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">{t("maintConstruct.reportTitle")}</h2>
                        <span className="gold-divider" />
                        <p className="text-navy/75 leading-relaxed">{t("maintConstruct.reportBody")}</p>
                        <div className="aspect-[4/3] overflow-hidden mt-8">
                            <img src={REPORT_IMAGE} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </Reveal>
                    <div className="lg:col-span-7 space-y-px bg-navy/10">
                        {REPORT_KEYS.map((k, i) => {
                            const Icon = REPORT_ICONS[i] || Camera;
                            return (
                                <Reveal key={k} delay={i * 70} className="bg-pearl p-8 flex items-start gap-6" data-testid={`mc-report-${i}`}>
                                    <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 border border-gold/40">
                                        <Icon className="w-5 h-5 text-gold" strokeWidth={1.25} />
                                    </div>
                                    <div>
                                        <h3 className="font-serif text-xl mb-1">{t(`maintConstruct.${k}.label`)}</h3>
                                        <p className="text-sm text-navy/70 leading-relaxed">{t(`maintConstruct.${k}.body`)}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 6 — Why PKM */}
            <section className="bg-sand/50 py-24 sm:py-32" data-testid="mc-why-section">
                <div className="container-luxe">
                    <Reveal className="max-w-3xl mb-20">
                        <p className="overline text-gold">{t("maintConstruct.whyEyebrow")}</p>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-tight">{t("maintConstruct.whyTitle")}</h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
                        {WHY_KEYS.map((k, i) => {
                            const Icon = WHY_ICONS[i] || Eye;
                            return (
                                <Reveal key={k} delay={i * 80} data-testid={`mc-why-${i}`}>
                                    <Icon className="w-7 h-7 text-gold mb-5" strokeWidth={1.25} />
                                    <h3 className="font-serif text-2xl mb-3">{t(`maintConstruct.${k}.title`)}</h3>
                                    <span className="block w-8 h-px bg-gold mb-4" />
                                    <p className="text-navy/70 text-sm leading-relaxed">{t(`maintConstruct.${k}.body`)}</p>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SECTION 7 — CTA */}
            <section className="bg-navy text-pearl py-24 text-center" data-testid="mc-cta-section">
                <div className="container-luxe max-w-2xl mx-auto space-y-7">
                    <p className="overline text-gold">{t("maintConstruct.outroEyebrow")}</p>
                    <h2 className="font-serif text-pearl text-4xl sm:text-5xl leading-tight">{t("maintConstruct.outroTitle")}</h2>
                    <p className="text-pearl/75 leading-relaxed">{t("maintConstruct.outroBody")}</p>
                    <Link to="/contact" className="btn-luxe-light" data-testid="mc-cta-button">
                        {t("maintConstruct.outroCta")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
